import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Pause, Play, Heart, Send, Star, Eye, Music2, Crown } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * StoryViewer — full-screen Instagram-style viewer.
 *
 *   · Auto-advances through each story in the active bubble.
 *   · Tap left/right zones or arrow keys to navigate within a bubble.
 *   · Swipe/arrow at edges jumps to next/previous bubble.
 *   · Progress bars at the top mirror the active story's duration.
 */
export default function StoryViewer({ bubbles, startIndex, onClose, onStorySeen }) {
  const { user } = useAuth();
  const [bIdx, setBIdx] = useState(startIndex);
  const [sIdx, setSIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [reactions, setReactions] = useState({ count: 0, mine: null });
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [heartBurst, setHeartBurst] = useState(false);
  const [highlightPickerOpen, setHighlightPickerOpen] = useState(false);
  const [interactive, setInteractive] = useState(null);   // {interactive, counts, total, picked, correct?}
  const [viewersOpen, setViewersOpen] = useState(false);
  const [viewers, setViewers] = useState({ count: 0, view_count_total: 0, viewers: [] });
  const [transcript, setTranscript] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const videoRef = useRef(null);
  const rafRef = useRef(null);
  const startTimeRef = useRef(0);
  const lastTapRef = useRef(0);
  const onStorySeenRef = useRef(onStorySeen);
  useEffect(() => { onStorySeenRef.current = onStorySeen; }, [onStorySeen]);

  const bubble = bubbles[bIdx];
  const story = bubble?.stories?.[sIdx];
  const total = bubble?.stories?.length || 0;
  const isOwner = user && story && user.user_id === story.creator_id;

  // Kick view-marker once per story as soon as it starts. Also refetch the
  // reaction summary so the heart reflects my prior double-tap.
  useEffect(() => {
    if (!story?.id) return;
    api.post(`/community/stories/${story.id}/view`).catch(() => {});
    onStorySeenRef.current && onStorySeenRef.current(story.id);
    setProgress(0);
    setReactions({ count: 0, mine: null });
    setReplyText("");
    setInteractive(null);
    setTranscript(null);
    startTimeRef.current = performance.now();
    api.get(`/community/stories/${story.id}/reactions`).then((r) => setReactions(r.data || { count: 0, mine: null })).catch(() => {});
    api.get(`/community/stories/${story.id}/interact`).then((r) => {
      if (r.data?.interactive) setInteractive(r.data);
    }).catch(() => {});
    // For voice stories, preload any cached transcript.
    if (story.audio_only || story.format === "voice") {
      api.get(`/community/stories/${story.id}/transcript`).then((r) => {
        if (r.data?.transcript) setTranscript(r.data.transcript);
      }).catch(() => {});
    }
  }, [story?.id]);

  // Progress ticker — decoupled from video element so thumbnail-only
  // stories (and broken media) still auto-advance.
  useEffect(() => {
    if (!story) return;
    const duration = Math.max(3, Math.min(15, story.duration_sec || 8)) * 1000;
    const tick = (now) => {
      if (paused) {
        startTimeRef.current = now - progress * duration;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const elapsed = now - startTimeRef.current;
      const p = Math.min(1, elapsed / duration);
      setProgress(p);
      if (p >= 1) {
        advance(+1);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line
  }, [story?.id, paused]);

  const advance = (dir) => {
    if (!bubble) return;
    const next = sIdx + dir;
    if (next >= 0 && next < total) {
      setSIdx(next);
      return;
    }
    // Cross bubble boundary
    const nextB = bIdx + dir;
    if (nextB < 0 || nextB >= bubbles.length) { onClose(); return; }
    setBIdx(nextB);
    setSIdx(dir > 0 ? 0 : Math.max(0, (bubbles[nextB].stories?.length || 1) - 1));
  };

  const toggleHeart = async () => {
    if (!user) { toast.error("Sign in to react"); return; }
    if (!story) return;
    const willAdd = !reactions.mine;
    // Optimistic
    setReactions((r) => ({ count: r.count + (willAdd ? 1 : -1), mine: willAdd ? "❤️" : null }));
    if (willAdd) {
      setHeartBurst(true);
      setTimeout(() => setHeartBurst(false), 700);
    }
    try {
      if (willAdd) {
        await api.post(`/community/stories/${story.id}/react`, { emoji: "❤️" });
      } else {
        await api.delete(`/community/stories/${story.id}/react`);
      }
    } catch (e) {
      // Rollback
      setReactions((r) => ({ count: r.count + (willAdd ? -1 : 1), mine: willAdd ? null : "❤️" }));
      toast.error(formatApiError(e));
    }
  };

  // Double-tap on media = heart
  const onMediaTap = () => {
    const now = Date.now();
    const delta = now - lastTapRef.current;
    lastTapRef.current = now;
    if (delta < 350 && delta > 0) {
      if (!reactions.mine) toggleHeart();
      else {
        setHeartBurst(true);
        setTimeout(() => setHeartBurst(false), 700);
      }
    }
  };

  const sendReply = async () => {
    if (!user) { toast.error("Sign in to reply"); return; }
    const t = replyText.trim();
    if (!t) return;
    setSendingReply(true);
    try {
      await api.post(`/community/stories/${story.id}/reply`, { text: t });
      toast.success("Sent to " + (bubble?.owner_name || "creator"));
      setReplyText("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingReply(false); }
  };

  const pickInteractive = async (idx) => {
    if (!user) { toast.error("Sign in to vote"); return; }
    if (interactive?.picked != null) return;   // already answered
    try {
      const { data } = await api.post(`/community/stories/${story.id}/interact`, { option_index: idx });
      setInteractive((s) => ({ ...s, counts: data.counts, total: data.total, picked: data.picked, correct_index: data.correct_index, correct: data.correct }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openViewers = async () => {
    if (!isOwner) return;
    setViewersOpen(true);
    try {
      const { data } = await api.get(`/community/stories/${story.id}/viewers`);
      setViewers(data || { count: 0, view_count_total: 0, viewers: [] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const transcribeVoice = async () => {
    if (!story?.id) return;
    setTranscribing(true);
    try {
      const { data } = await api.post(`/community/stories/${story.id}/transcribe`);
      setTranscript(data?.transcript || "(no speech detected)");
      toast.success(data?.cached ? "Loaded cached transcript" : "Transcribed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTranscribing(false); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") advance(+1);
      else if (e.key === "ArrowLeft") advance(-1);
      else if (e.key === "Escape") onClose();
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [bIdx, sIdx, total]);

  if (!bubble || !story) return null;

  return (
    <div
      data-testid="story-viewer"
      className="fixed inset-0 z-[70] bg-black flex items-center justify-center select-none"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Backdrop tap → close */}
      <button
        data-testid="story-viewer-close"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
      ><X className="w-5 h-5" /></button>

      <button
        onClick={() => setPaused((p) => !p)}
        data-testid="story-viewer-pause"
        className="absolute top-4 right-16 z-10 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
      >{paused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}</button>

      {/* Left/right nav (desktop) */}
      <button
        data-testid="story-viewer-prev"
        onClick={() => advance(-1)}
        className="absolute left-4 z-10 text-white/60 hover:text-white bg-white/5 hover:bg-white/15 rounded-full p-2 hidden md:block"
      ><ChevronLeft className="w-6 h-6" /></button>
      <button
        data-testid="story-viewer-next"
        onClick={() => advance(+1)}
        className="absolute right-4 z-10 text-white/60 hover:text-white bg-white/5 hover:bg-white/15 rounded-full p-2 hidden md:block"
      ><ChevronRight className="w-6 h-6" /></button>

      <div className="relative w-full h-full max-w-md mx-auto">
        {/* Progress bars */}
        <div className="absolute top-2 inset-x-3 z-10 flex gap-1">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-white/20 rounded overflow-hidden">
              <div
                className="h-full bg-white transition-none"
                style={{ width: `${i < sIdx ? 100 : i === sIdx ? progress * 100 : 0}%` }}
              />
            </div>
          ))}
        </div>

        {/* Owner header */}
        <div className="absolute top-5 left-3 right-16 z-10 flex items-center gap-2 pt-2">
          {bubble.owner_avatar && <img src={bubble.owner_avatar} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-black" />}
          {(bubble.collab_avatars || []).slice(0, 2).map((pic, i) => (
            <img
              key={i}
              data-testid={`story-collab-avatar-${i}`}
              src={pic}
              alt=""
              className="w-7 h-7 rounded-full object-cover -ml-4 ring-2 ring-black"
              style={{ zIndex: 5 - i }}
            />
          ))}
          <div className="text-white font-bold text-sm truncate ml-1">{bubble.owner_name}</div>
          {(bubble.collab_avatars || []).length > 0 && (
            <span data-testid="story-collab-tag" className="text-[9px] bg-white/15 text-white px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider">Collab</span>
          )}
          {bubble.is_live && (
            <span className="bg-tv-live text-white text-[9px] font-black uppercase px-1.5 py-0.5 rounded tracking-wider">LIVE</span>
          )}
          {story.from_live_snippet && (
            <span data-testid="story-snippet-tag" className="text-[9px] bg-yellow-500/30 text-yellow-300 px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider">Snippet</span>
          )}
        </div>

        {/* Media */}
        <div className="absolute inset-0 flex items-center justify-center" onClick={onMediaTap}>
          {story.audio_only || story.format === "voice" ? (
            <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-600 via-orange-500 to-yellow-400 flex items-center justify-center">
              <div data-testid="story-voice-wave" className="flex items-end gap-1.5 h-32 px-10">
                {Array.from({ length: 28 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-2 rounded-full bg-white"
                    style={{
                      height: `${25 + Math.sin(i * 0.8) * 35 + Math.random() * 20}px`,
                      animation: `voice-wave 1.2s ease-in-out ${i * 40}ms infinite alternate`,
                    }}
                  />
                ))}
              </div>
              {story.video_url && (
                <audio key={`v-${story.id}`} src={story.video_url} autoPlay className="hidden" />
              )}
            </div>
          ) : story.video_url ? (
            <video
              ref={videoRef}
              key={story.id}
              src={story.video_url}
              autoPlay
              playsInline
              muted={false}
              loop={false}
              className="max-w-full max-h-full w-full h-full object-contain bg-black"
              onError={() => {/* just let the progress timer advance */}}
            />
          ) : story.thumbnail_url ? (
            <img src={story.thumbnail_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="text-white/40">No media</div>
          )}
          {story.music?.preview_url && !story.audio_only && (
            <audio
              key={`music-${story.id}`}
              src={story.music.preview_url}
              autoPlay
              loop
              className="hidden"
            />
          )}
        </div>

        {/* Heart burst overlay */}
        {heartBurst && (
          <div
            data-testid="story-heart-burst"
            className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
          >
            <Heart className="w-32 h-32 fill-pink-500 text-pink-500 drop-shadow-2xl animate-ping" />
          </div>
        )}

        {/* Tap zones (mobile) — sit BELOW the media click so double-tap still registers */}
        <button className="absolute left-0 top-[15%] w-1/3 h-[60%] z-[5]" onClick={(e) => { e.stopPropagation(); advance(-1); }} aria-label="Previous" />
        <button className="absolute right-0 top-[15%] w-1/3 h-[60%] z-[5]" onClick={(e) => { e.stopPropagation(); advance(+1); }} aria-label="Next" />

        {/* Music sticker chip — always visible, top-right under header */}
        {story.music && (
          <div
            data-testid="story-music-sticker"
            className="absolute top-16 right-3 z-15 flex items-center gap-2 bg-black/50 backdrop-blur-md rounded-full pl-1 pr-3 py-1 border border-white/10 max-w-[60%]"
            onClick={(e) => e.stopPropagation()}
          >
            {story.music.album_art ? (
              <img src={story.music.album_art} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center"><Music2 className="w-4 h-4 text-white" /></div>
            )}
            <div className="min-w-0">
              <div className="text-white text-xs font-bold truncate">{story.music.title}</div>
              <div className="text-white/70 text-[10px] truncate">{story.music.artist}</div>
            </div>
          </div>
        )}

        {/* Voice transcript overlay — for voice stories only */}
        {(story.audio_only || story.format === "voice") && (
          <div data-testid="story-transcript-panel" className="absolute left-3 right-3 bottom-20 z-20" onClick={(e) => e.stopPropagation()}>
            {transcript ? (
              <div data-testid="story-transcript" className="bg-black/65 backdrop-blur-md rounded-xl px-3 py-2 text-white text-sm leading-relaxed max-h-40 overflow-y-auto border border-white/10">
                {transcript}
              </div>
            ) : isOwner ? (
              <button
                data-testid="story-transcribe-btn"
                onClick={transcribeVoice}
                disabled={transcribing}
                className="w-full bg-black/60 backdrop-blur-md hover:bg-black/80 disabled:opacity-50 border border-white/15 text-white font-bold text-sm px-3 py-2 rounded-xl"
              >{transcribing ? "Transcribing…" : "Add transcript with Whisper"}</button>
            ) : null}
          </div>
        )}

        {story.title && (
          <div className="absolute bottom-24 inset-x-4 z-10 text-white font-bold drop-shadow text-base">
            {story.title}
          </div>
        )}

        {/* Interactive overlay — poll or quiz */}
        {interactive?.interactive && (
          <div
            data-testid="story-interactive"
            className="absolute left-4 right-4 top-1/2 -translate-y-1/2 z-20 pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-black/55 backdrop-blur-md rounded-2xl p-4 border border-white/15">
              <div className="text-white font-display font-bold text-lg leading-tight mb-3 drop-shadow">{interactive.interactive.question}</div>
              <div className="space-y-2">
                {interactive.interactive.options.map((opt, i) => {
                  const answered = interactive.picked != null;
                  const pct = answered ? Math.round(((interactive.counts?.[i] || 0) / Math.max(1, interactive.total || 1)) * 100) : 0;
                  const isCorrect = interactive.interactive.type === "quiz" && interactive.correct_index === i;
                  const isPicked = interactive.picked === i;
                  const isQuiz = interactive.interactive.type === "quiz";
                  const bgClass = !answered
                    ? "bg-white/15 hover:bg-white/25"
                    : isQuiz
                      ? (isCorrect ? "bg-green-500/70 ring-2 ring-green-300" : (isPicked ? "bg-red-500/60" : "bg-white/10"))
                      : (isPicked ? "bg-tv-accent/80 ring-2 ring-tv-accent" : "bg-white/10");
                  return (
                    <button
                      key={i}
                      data-testid={`story-interactive-option-${i}`}
                      disabled={answered}
                      onClick={() => pickInteractive(i)}
                      className={`relative w-full text-left rounded-xl px-3 py-2 font-bold text-white text-sm transition ${bgClass} ${!answered ? "cursor-pointer" : "cursor-default"}`}
                    >
                      {answered && (
                        <div
                          className="absolute inset-y-0 left-0 rounded-xl bg-white/10"
                          style={{ width: `${pct}%` }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-between gap-2">
                        <span>{opt}</span>
                        {answered && <span className="text-xs text-white/80">{pct}%</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
              {interactive.picked != null && interactive.interactive.type === "quiz" && (
                <div data-testid="story-interactive-result" className="mt-3 text-center text-sm font-bold">
                  {interactive.correct ? <span className="text-green-300">✔ Correct!</span> : <span className="text-red-300">✗ Nice try — correct answer highlighted</span>}
                </div>
              )}
              {interactive.total > 0 && (
                <div className="text-[10px] text-white/60 text-center mt-2 uppercase tracking-wider">{interactive.total} vote{interactive.total === 1 ? "" : "s"}</div>
              )}
            </div>
          </div>
        )}

        {/* Bottom reaction + reply bar */}
        <div className="absolute bottom-0 inset-x-0 z-20 p-3 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center gap-2">
            {isOwner ? (
              <>
                <button
                  data-testid="story-viewer-viewers"
                  onClick={openViewers}
                  className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-bold px-3 py-2 rounded-full"
                >
                  <Eye className="w-4 h-4" /> Viewers
                </button>
                <button
                  data-testid="story-viewer-highlight"
                  onClick={() => setHighlightPickerOpen(true)}
                  className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-bold px-3 py-2 rounded-full"
                >
                  <Star className="w-4 h-4" /> Save to highlight
                </button>
                <div className="text-white/60 text-xs flex items-center gap-1 ml-auto">
                  <Heart className="w-3.5 h-3.5" fill="currentColor" /> {reactions.count}
                </div>
              </>
            ) : (
              <>
                <input
                  data-testid="story-viewer-reply-input"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !sendingReply) sendReply(); }}
                  placeholder={`Send a message to ${bubble?.owner_name?.split(" ")[0] || "creator"}…`}
                  className="flex-1 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-white text-sm placeholder:text-white/50 outline-none focus:border-tv-accent"
                />
                <button
                  data-testid="story-viewer-send-reply"
                  onClick={sendReply}
                  disabled={sendingReply || !replyText.trim()}
                  className="bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black p-2 rounded-full"
                  aria-label="Send reply"
                ><Send className="w-4 h-4" /></button>
                <button
                  data-testid="story-viewer-heart"
                  onClick={toggleHeart}
                  className={`p-2 rounded-full transition ${reactions.mine ? "bg-pink-500 text-white" : "bg-white/10 hover:bg-white/20 text-white"}`}
                  aria-label="Heart"
                >
                  <Heart className="w-5 h-5" fill={reactions.mine ? "currentColor" : "none"} />
                </button>
                {reactions.count > 0 && (
                  <span data-testid="story-viewer-heart-count" className="text-white/80 text-xs font-bold min-w-[1.5em]">{reactions.count}</span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {highlightPickerOpen && (
        <HighlightPicker
          story={story}
          onClose={() => setHighlightPickerOpen(false)}
          onSaved={() => { setHighlightPickerOpen(false); toast.success("Pinned to highlights"); }}
        />
      )}

      {viewersOpen && (
        <div
          data-testid="story-viewers-drawer"
          className="absolute inset-0 z-40 bg-black/80 flex items-end sm:items-center justify-center p-4"
          onClick={() => setViewersOpen(false)}
        >
          <div
            className="bg-[#111] border border-white/10 rounded-2xl shadow-2xl p-5 w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black text-xl text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-tv-accent" /> Viewers
              </h3>
              <button onClick={() => setViewersOpen(false)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-white/60 text-xs uppercase tracking-wider mb-3">
              {viewers.count} unique · {viewers.view_count_total} total taps
            </div>
            {viewers.breakdown && (viewers.breakdown.subscribers > 0 || viewers.breakdown.followers_only > 0) && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {viewers.breakdown.subscribers > 0 && (
                  <span data-testid="viewers-subscriber-chip" className="text-[10px] font-black uppercase tracking-wider bg-tv-accent/20 text-tv-accent px-2 py-1 rounded-full">
                    {viewers.breakdown.subscribers} subscriber{viewers.breakdown.subscribers === 1 ? "" : "s"}
                  </span>
                )}
                {viewers.breakdown.followers_only > 0 && (
                  <span data-testid="viewers-follower-chip" className="text-[10px] font-black uppercase tracking-wider bg-fuchsia-500/20 text-fuchsia-300 px-2 py-1 rounded-full">
                    {viewers.breakdown.followers_only} follower{viewers.breakdown.followers_only === 1 ? "" : "s"}
                  </span>
                )}
                {viewers.breakdown.others > 0 && (
                  <span className="text-[10px] font-black uppercase tracking-wider bg-white/10 text-white/60 px-2 py-1 rounded-full">
                    {viewers.breakdown.others} other{viewers.breakdown.others === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            )}
            {viewers.viewers.length === 0 ? (
              <div data-testid="story-viewers-empty" className="text-white/60 text-sm py-6 text-center">No one's seen this yet. Hang tight.</div>
            ) : (
              <div className="space-y-2">
                {viewers.viewers.map((v, i) => (
                  <div key={`${v.user_id}-${i}`} data-testid={`story-viewer-row-${v.user_id}`} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2">
                    {v.picture ? (
                      <img src={v.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/60 font-bold">{(v.name || "?")[0]}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="text-white font-bold text-sm truncate">{v.name}</div>
                        {v.is_subscriber && (
                          <span data-testid={`viewer-sub-${v.user_id}`} title="Subscriber" className="inline-flex items-center gap-0.5 text-[9px] bg-tv-accent text-black px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider">
                            <Crown className="w-2.5 h-2.5" /> Sub
                          </span>
                        )}
                        {v.is_follower && !v.is_subscriber && (
                          <span data-testid={`viewer-follower-${v.user_id}`} title="Follower" className="text-[9px] bg-fuchsia-500/30 text-fuchsia-300 px-1.5 py-0.5 rounded-full font-black uppercase tracking-wider">
                            Follower
                          </span>
                        )}
                      </div>
                      <div className="text-white/50 text-xs">{new Date(v.seen_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HighlightPicker({ story, onClose, onSaved }) {
  const [highlights, setHighlights] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/creators/${story.creator_id}/highlights`).then((r) => setHighlights(r.data || [])).catch(() => {});
  }, [story.creator_id]);

  const addToExisting = async (h) => {
    setBusy(true);
    try {
      const sids = Array.from(new Set([...(h.story_ids || []), story.id]));
      await api.patch(`/creator/highlights/${h.id}`, { story_ids: sids });
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const createNew = async () => {
    if (!newTitle.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      await api.post(`/creator/highlights`, {
        title: newTitle.trim(),
        story_ids: [story.id],
        cover_url: story.thumbnail_url || null,
      });
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="highlight-picker" className="absolute inset-0 z-40 bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl shadow-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-black text-xl text-white mb-3">Pin to Highlight</h3>
        {highlights.length > 0 && (
          <div className="space-y-1.5 mb-4 max-h-48 overflow-y-auto">
            {highlights.map((h) => (
              <button
                key={h.id}
                data-testid={`highlight-pick-${h.id}`}
                onClick={() => addToExisting(h)}
                disabled={busy}
                className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2.5 text-left"
              >
                {h.cover_url ? <img src={h.cover_url} alt="" className="w-10 h-10 rounded object-cover" /> : <Star className="w-5 h-5 text-tv-accent" />}
                <div className="flex-1 text-white font-bold truncate">{h.title}</div>
                <div className="text-white/50 text-xs">{(h.story_ids || []).length}</div>
              </button>
            ))}
          </div>
        )}
        <div className="border-t border-white/10 pt-4">
          <div className="text-white/60 text-xs uppercase tracking-wider font-bold mb-2">New highlight</div>
          <div className="flex gap-2">
            <input
              data-testid="highlight-new-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              maxLength={60}
              placeholder="Highlight title · e.g. Best of May"
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent"
            />
            <button
              data-testid="highlight-create-btn"
              onClick={createNew}
              disabled={busy || !newTitle.trim()}
              className="bg-tv-accent disabled:opacity-40 hover:bg-tv-accent/90 text-black font-bold px-4 py-2 rounded-lg text-sm"
            >Create</button>
          </div>
        </div>
      </div>
    </div>
  );
}
